import * as React from 'react';

import { CurrencyCode } from '@rematter/frontend-lib-api-client';
import { Badge, DataGridColumn, LocalizedDate } from '@rematter/ui-web';

import { PaymentLinkedDocuments } from '../../payments/PaymentLinkedDocuments';
import { PaymentStatusBadge } from '../../payments/PaymentStatusBadge';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { PaymentRecord } from '~/modules/finance/types';
import { getLocale } from '~/shared/utils/locale';

type UseDrawerPaymentColumnsArgs = {
  currency: CurrencyCode;
};

export const useDrawerPaymentColumns = ({
  currency,
}: UseDrawerPaymentColumnsArgs): DataGridColumn<PaymentRecord>[] => {
  const t = useTranslations();

  return React.useMemo(
    () => [
      {
        key: 'transactionDate',
        header: t.columns.date,
        sortable: true,
        cell: ({ row: record }: { row: PaymentRecord }) => (
          <span>
            <LocalizedDate date={record.transactionDate} dateStyle="short" />
          </span>
        ),
      },
      {
        key: 'documentNumber',
        header: t.columns.documentNumber,
        width: 100,
        cell: ({ row: record }: { row: PaymentRecord }) => (
          <span className="font-medium">{record.documentNumber ?? '-'}</span>
        ),
      },
      {
        key: 'paymentType',
        header: t.columns.type,
        cell: ({ row: record }: { row: PaymentRecord }) => (
          <Badge
            color={record.paymentType.includes('Advance') ? 'teal' : 'neutral'}
          >
            {t.enums.paymentType[record.paymentType] ?? record.paymentType}
          </Badge>
        ),
      },
      {
        key: 'accountName',
        header: t.columns.account,
        resizable: true,
        grow: 1,
        cell: ({ row: record }: { row: PaymentRecord }) =>
          record.accountName ?? '-',
      },
      {
        key: 'paymentMethod',
        header: t.columns.method,
        cell: ({ row: record }: { row: PaymentRecord }) => (
          <span>
            {t.payments.list.methodFilter.options[record.paymentMethod] ??
              record.paymentMethod}
          </span>
        ),
      },
      {
        key: 'status',
        header: t.columns.status,
        sortable: true,
        cell: ({ row: record }: { row: PaymentRecord }) => (
          <PaymentStatusBadge status={record.status} />
        ),
      },
      {
        key: 'amount',
        header: t.columns.amount,
        sortable: true,
        cell: ({ row: record }: { row: PaymentRecord }) => (
          <span
            className={
              record.amount.amount < 0 ? 'text-failure/60' : 'text-success/60'
            }
          >
            {new Intl.NumberFormat(getLocale(), {
              style: 'currency',
              currency: record.amount.currencyCode,
            }).format(record.amount.amount)}
          </span>
        ),
      },
      {
        key: 'unappliedAmount',
        header: t.columns.unapplied,
        cell: ({ row: record }: { row: PaymentRecord }) => {
          if (record.unappliedAmount.amount === 0) {
            return <span className="text-neutral/60">-</span>;
          }
          return (
            <span className="text-warning">
              {new Intl.NumberFormat(getLocale(), {
                style: 'currency',
                currency: record.unappliedAmount.currencyCode,
              }).format(Math.abs(record.unappliedAmount.amount))}
            </span>
          );
        },
      },
      {
        key: 'linkedDocuments',
        header: t.payments.list.columns.linkedDocuments,
        width: 200,
        cell: ({ row: record }: { row: PaymentRecord }) => (
          <PaymentLinkedDocuments
            linkedBills={record.linkedBills}
            linkedInvoices={record.linkedInvoices}
          />
        ),
      },
    ],
    [t, currency],
  );
};
